import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import {
  PlayCircleOutline as PlayIcon,
  SpaOutlined,
  InfoOutlined,
  CheckCircleOutline
} from '@mui/icons-material';
import relaxationFallback from '../assets/images/relaxation1.jpg';

const RelaxationList = ({ relaxations, onStartTracking }) => {
  const theme = useTheme();

  if (!relaxations?.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 10, px: 3 }}>
        <SpaOutlined
          sx={{
            fontSize: 64,
            color: alpha(theme.palette.primary.main, 0.2),
            mb: 2
          }}
        />
        <Typography variant="h6" color="text.primary" gutterBottom>
          No relaxation exercises yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your relaxation sessions will appear here once added.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      {relaxations.map((item) => {
        const isCompleted = item.completed === 1;

        return (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: 160,
                  overflow: 'hidden'
                }}
              >
                <img
                  src={item.image || relaxationFallback}
                  alt={item.track_name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'transform 0.5s ease'
                  }}
                  onError={(e) => (e.target.src = relaxationFallback)}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 50%)'
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    left: 16,
                    right: 16,
                    color: 'white',
                    fontWeight: 700,
                    textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                    lineHeight: 1.3
                  }}
                >
                  {item.track_name}
                </Typography>

                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    px: 1.6,
                    py: 0.5,
                    borderRadius: 999,
                    fontSize: '0.775rem',
                    fontWeight: 700,
                    backgroundColor: isCompleted ? '#4caf50' : '#ef5350',
                    color: '#ffffff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.28)',
                    minWidth: 105,
                    textAlign: 'center',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                  }}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircleOutline sx={{ fontSize: '1rem' }} />
                      Completed
                    </>
                  ) : (
                    'Not Completed'
                  )}
                </Box>
              </Box>

              <CardContent sx={{ flexGrow: 1, px: 2, py: 1.75 }}>
                {item.instructions && (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      <InfoOutlined fontSize="inherit" /> Instructions
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {item.instructions}
                    </Typography>
                  </>
                )}

                {item.benefits && (
                  <>
                    <Typography variant="caption" fontWeight={600}>
                      <SpaOutlined fontSize="inherit" /> Benefits
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(() => {
                        let benefitsArray = [];

                        if (Array.isArray(item.benefits)) {
                          benefitsArray = item.benefits;
                        } else if (typeof item.benefits === 'string') {
                          try {
                            benefitsArray = JSON.parse(item.benefits);
                          } catch {
                            benefitsArray = [item.benefits];
                          }
                        }

                        return (
                          benefitsArray.slice(0, 2).join(' • ') +
                          (benefitsArray.length > 2 ? '…' : '')
                        );
                      })()}
                    </Typography>
                  </>
                )}
              </CardContent>

              <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color={isCompleted ? 'success' : 'primary'}
                  startIcon={
                    isCompleted ? (
                      <CheckCircleOutline />
                    ) : (
                      <PlayIcon />
                    )
                  }
                  onClick={() => onStartTracking(item)}
                >
                  {isCompleted
                    ? 'Re-practice Relaxation'
                    : 'Start Relaxation'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default RelaxationList;